import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/utils/api";
import { formatCurrency as sharedFormatCurrency } from "@/utils/format";
import { trackEvent } from "@/lib/analytics";
import YearSelector from "@/components/YearSelector";
import ConfirmDialog from "@/components/ConfirmDialog";
import ExpenseDetail from "@/components/ExpenseDetail";
import IntegrityReviewBanner from "@/components/IntegrityReviewBanner";
import ExpenseExportDialog from "@/components/ExpenseExportDialog";
import AuditBreakdownPanel from "@/components/AuditBreakdownPanel";
import { getLegalRef } from "@/utils/legalRefs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { TrendingUp, TrendingDown, DollarSign, Plus, Edit, Trash2, FileText, Upload, CheckCircle, XCircle, Search, Download, MessageSquare, Lock, Unlock, Boxes, Repeat, Check, AlertTriangle, Ban, RotateCcw, Mail, Smartphone, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import ReceiptUpload from "@/components/ReceiptUpload";
import UpgradePromptModal from "@/components/UpgradePromptModal";
import DuplicateWarningDialog from "@/components/DuplicateWarningDialog";
import BatchReceiptDropzone from "@/components/BatchReceiptDropzone";
import MobileScanPromo from "@/components/MobileScanPromo";
import ExternalInvoiceModal from "@/components/finance/ExternalInvoiceModal";

// Juni 2026 — Vorschau der nächsten Fälligkeit für wiederkehrende Ausgaben (rein anzeigend,
// keine Buchung). Spiegelt die Backend-Logik (Termin relativ zum Anker, Monatsend-Clamp).
const _addMonthsJS = (date, months) => {
  const r = new Date(date);
  const day = r.getDate();
  r.setDate(1);
  r.setMonth(r.getMonth() + months);
  const lastDay = new Date(r.getFullYear(), r.getMonth() + 1, 0).getDate();
  r.setDate(Math.min(day, lastDay));
  return r;
};

const nextRecurrence = (item) => {
  if (!item?.is_recurring || !item?.recurrence_interval || !item?.date) return null;
  const anchor = new Date(item.date);
  if (isNaN(anchor.getTime())) return null;
  const end = item.recurrence_end_date ? new Date(item.recurrence_end_date) : null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const monthsMap = { monthly: 1, half_yearly: 6, yearly: 12 };
  for (let i = 1; i <= 600; i++) {
    let occ;
    if (monthsMap[item.recurrence_interval]) {
      occ = _addMonthsJS(anchor, monthsMap[item.recurrence_interval] * i);
    } else if (item.recurrence_interval === "custom") {
      const n = parseInt(item.recurrence_custom_value) || 1;
      if (item.recurrence_custom_unit === "weeks") {
        occ = new Date(anchor);
        occ.setDate(occ.getDate() + 7 * n * i);
      } else {
        occ = _addMonthsJS(anchor, n * i);
      }
    } else {
      return null;
    }
    if (end && occ > end) return null;
    if (occ >= today) return occ;
  }
  return null;
};

// Juni 2026 — Multi-Steuersatz aus KI-Belegextraktion (tax_breakdown) für den Web-Upload.
// Bei >=2 echten Sätzen: tax_breakdown + Netto-Summe + dominanter Satz, sonst Einzel-Satz.
const buildExpenseTaxFields = (ed, defaultVat) => {
  const bd = Array.isArray(ed.tax_breakdown)
    ? ed.tax_breakdown
        .filter((tb) => tb && (Number(tb.net) > 0 || Number(tb.vat) > 0))
        .map((tb) => ({
          rate: Number(tb.rate) || 0,
          net: Math.round((Number(tb.net) || 0) * 100) / 100,
          vat: Math.round((Number(tb.vat) || 0) * 100) / 100,
        }))
    : [];
  if (bd.length >= 2) {
    const sumNet = Math.round(bd.reduce((s, tb) => s + tb.net, 0) * 100) / 100;
    const dom = [...bd].sort((a, b) => (b.net + b.vat) - (a.net + a.vat))[0];
    return { tax_breakdown: bd, multi_tax_suspected: true, net_amount: sumNet, vat_rate: dom ? dom.rate : defaultVat };
  }
  const vr = (ed.vat_rate && (ed.vat_rate_confidence === undefined || ed.vat_rate_confidence >= 60)) ? ed.vat_rate : defaultVat;
  return { vat_rate: vr, net_amount: ed.net_amount ?? null };
};

export default function Finances() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [income, setIncome] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedIncome, setSelectedIncome] = useState(null);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [incomeDialogOpen, setIncomeDialogOpen] = useState(false);
  const [externalInvoiceOpen, setExternalInvoiceOpen] = useState(false);
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [receiptUploadOpen, setReceiptUploadOpen] = useState(false);
  const [upgradePromptOpen, setUpgradePromptOpen] = useState(false);
  const [countryInfo, setCountryInfo] = useState(null);
  const [userPlan, setUserPlan] = useState('free');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [confirmDialog, setConfirmDialog] = useState({ 
    isOpen: false, 
    type: null, // 'income' or 'expense'
    itemId: null,
    title: '',
    description: ''
  });
  const [expenseDetailOpen, setExpenseDetailOpen] = useState(false);
  const [selectedExpenseDetail, setSelectedExpenseDetail] = useState(null);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [expenseSearchQuery, setExpenseSearchQuery] = useState("");
  // Juni 2026 — Mehrfach-Quickfilter für die Ausgabenliste (Set aus: booked | pending | afa).
  // Default: alle drei aktiv → zeigt alles ("Alle" entfällt dadurch).
  const [expenseFilters, setExpenseFilters] = useState(() => new Set(["booked", "pending", "afa", "deactivated"]));
  // Juli 2026 — Beleg-Eingang per E-Mail: orthogonaler "Posteingang"-Filter (nur unbestätigte E-Mail-Belege)
  const [inboxOnly, setInboxOnly] = useState(() => new URLSearchParams(window.location.search).get("filter") === "inbox");
  // Aug 2026 — „Geprüft“-Workflow (User-Wunsch): Nur-Ungeprüft-Quickfilter + Monatsfilter
  const [unreviewedOnly, setUnreviewedOnly] = useState(false);
  const [expenseMonth, setExpenseMonth] = useState("all");
  const [expenseVendor, setExpenseVendor] = useState("all");
  const toggleExpenseFilter = (key) => {
    setExpenseFilters((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  // Quickfilter Einnahmen: Aus Rechnung | Manuell | Importiert (default alle aktiv).
  const [incomeFilters, setIncomeFilters] = useState(() => new Set(["invoice", "manual", "imported", "deactivated"]));
  const toggleIncomeFilter = (key) => {
    setIncomeFilters((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const incomeCat = (it) => (it.deactivated_at ? "deactivated" : (it.auto_generated ? "invoice" : (it.source === "external_invoice" ? "imported" : "manual")));
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState({ open: false, matches: [] });
  const [sbExpenseComments, setSbExpenseComments] = useState({});
  // Feb 2026 — UStVA-konsistente Summen: USt-Werte kommen aus /api/tax/calculate
  // (= UStVA-Engine). So stimmen Finanzen-KPIs mit UStVA + Steuern-Tab überein.
  const [taxSummary, setTaxSummary] = useState(null);
  const [showLocked, setShowLocked] = useState(() => {
    try { return localStorage.getItem('ivoliq_show_locked') === '1'; } catch { return false; }
  });
  const toggleShowLocked = () => {
    setShowLocked(prev => {
      const next = !prev;
      try { localStorage.setItem('ivoliq_show_locked', next ? '1' : '0'); } catch {}
      return next;
    });
  };

  const [incomeForm, setIncomeForm] = useState({
    description: "",
    amount: "",
    date: new Date().toISOString().split('T')[0],
    category: "service",
    vat_rate: 0,
    project_id: ""
  });

  const [expenseForm, setExpenseForm] = useState({
    description: "",
    amount: "",
    date: new Date().toISOString().split('T')[0],
    category: "office",
    vat_rate: 0,
    is_deductible: true
  });

  const fetchCountryInfo = async () => {
    try {
      const response = await api.get('/country/info');
      setCountryInfo(response.data);
    } catch (error) {
      console.error("Error fetching country info:", error);
    }
  };

  const fetchUserPlan = async () => {
    try {
      const response = await api.get('/auth/me');
      setUserPlan(response.data.plan || 'free');
    } catch (error) {
      console.error("Error fetching user plan:", error);
    }
  };

  const fetchIncome = useCallback(async () => {
    try {
      const params = selectedYear ? `?year=${selectedYear}` : '';
      const response = await api.get(`/incomes${params}`);
      const sorted = (response.data || []).sort((a, b) => new Date(b.date) - new Date(a.date));
      setIncome(sorted);
    } catch (error) {
      console.error("Error fetching income:", error);
    }
  }, [selectedYear]);

  // Sep 2026 — unbestätigte Belege über ALLE Jahre (Prüf-Ansicht zeigt auch Belege mit
  // fremdem Jahr, die im Jahresfilter sonst „verschwinden" würden)
  const [inboxPending, setInboxPending] = useState([]);

  const fetchExpenses = useCallback(async () => {
    try {
      const params = selectedYear ? `?year=${selectedYear}` : '';
      const [response, pendingRes] = await Promise.all([
        api.get(`/expenses${params}`),
        api.get('/inbound/pending').catch(() => ({ data: [] })),
      ]);
      const sorted = (response.data || []).sort((a, b) => new Date(b.date) - new Date(a.date));
      setExpenses(sorted);
      setInboxPending(pendingRes.data || []);
    } catch (error) {
      console.error("Error fetching expenses:", error);
    }
  }, [selectedYear]);

  const fetchProjects = async () => {
    try {
      const response = await api.get('/projects');
      setProjects(response.data);
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  };

  // Feb 2026 — UStVA-Konsistenz: USt-Werte für die KPI-Kacheln aus /api/tax/calculate
  // ziehen (= UStVA-Engine). Brutto-Einnahmen werden weiter clientseitig aus den
  // sichtbaren db.income-Einträgen summiert (Konsistenz mit Liste). Beachtet
  // automatisch Soll-/Ist-Versteuerung via Backend-Settings.
  const fetchTaxSummary = useCallback(async () => {
    if (!selectedYear) return;
    try {
      const r = await api.get(`/tax/calculate?year=${selectedYear}`);
      setTaxSummary(r.data);
    } catch (e) {
      // 403 bei Free-Plan oder Country != DE/AT/CH → wir fallen leise auf Client-Summen zurück
      setTaxSummary(null);
    }
  }, [selectedYear]);

  useEffect(() => {
    fetchIncome();
    fetchExpenses();
    fetchProjects();
    fetchCountryInfo();
    fetchUserPlan();
    fetchExpenseCategories();
    fetchTaxSummary();
  }, [fetchIncome, fetchExpenses, fetchTaxSummary]);

  // Fetch SB comments for expenses
  useEffect(() => {
    if (!user?.id) return;
    api.get(`/advisor/clients/${user.id}/comments`).then(r => {
      const map = {};
      (r.data.comments || []).filter(c => c.entity_type === 'expense' && c.entity_id).forEach(c => {
        map[c.entity_id] = c;
      });
      setSbExpenseComments(map);
    }).catch(() => {});
  }, [user?.id]);

  const fetchExpenseCategories = async () => {
    try {
      const res = await api.get("/expense-categories");
      const all = [...(res.data.default || []), ...(res.data.custom || [])];
      setExpenseCategories(all);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const create = params.get("create");
    if (create === "income") {
      resetIncomeForm();
      setIncomeDialogOpen(true);
    }
    if (create === "expense") {
      resetExpenseForm();
      setExpenseDialogOpen(true);
    }
    // Feb 2026 — Stapel-Modus Tap-to-Edit: ?openExpense=<id> öffnet Detail-Sheet
    const openExpenseId = params.get("openExpense");
    if (openExpenseId) {
      api.get(`/expenses/${openExpenseId}`)
        .then((r) => {
          setSelectedExpenseDetail(r.data);
          setExpenseDetailOpen(true);
        })
        .catch(() => { /* ignore — expense may have been deleted */ });
    }
    if (create || openExpenseId) {
      params.delete("create");
      params.delete("openExpense");
      const nextSearch = params.toString();
      navigate({ search: nextSearch ? `?${nextSearch}` : "" }, { replace: true });
    }
  }, [location.search, navigate]);

  // Feb 2026 — Deep-Link Route /expense/:id öffnet das Detail-Sheet automatisch
  // (für SB-Kommentar-Links, Inbox-Widget). Nach dem Öffnen wird /finances übernommen.
  useEffect(() => {
    const m = location.pathname.match(/^\/expense\/([^/]+)/);
    if (!m) return;
    const expenseId = m[1];
    api.get(`/expenses/${expenseId}`)
      .then((r) => {
        setSelectedExpenseDetail(r.data);
        setExpenseDetailOpen(true);
      })
      .catch(() => {
        toast.error("Beleg nicht gefunden oder gelöscht.");
      });
    navigate("/finances", { replace: true });
  }, [location.pathname, navigate]);
  
  // Set default VAT rate when country info is loaded
  useEffect(() => {
    if (countryInfo && countryInfo.vat_rates && countryInfo.vat_rates.length > 0) {
      const defaultRate = countryInfo.vat_rates[0].rate;
      setIncomeForm(prev => ({ ...prev, vat_rate: prev.vat_rate === 0 ? defaultRate : prev.vat_rate }));
      setExpenseForm(prev => ({ ...prev, vat_rate: prev.vat_rate === 0 ? defaultRate : prev.vat_rate }));
    }
  }, [countryInfo]);

  const handleIncomeSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedIncome) {
        await api.put(`/incomes/${selectedIncome.id}`, incomeForm);
      } else {
        await api.post('/incomes', incomeForm);
      }
      toast.success(t('common.saved', 'Saved'));
      fetchIncome();
      setIncomeDialogOpen(false);
      resetIncomeForm();
    } catch (error) {
      console.error("Error saving income:", error);
      toast.error(t('finances.saveError', 'Error saving'));
    }
  };

  const handleExpenseSubmit = async (e, skipDuplicateCheck = false) => {
    if (e) e.preventDefault();
    
    // Duplicate check for new expenses
    if (!selectedExpense && !skipDuplicateCheck && expenseForm.amount && expenseForm.date) {
      try {
        const res = await api.post("/expenses/check-duplicate", {
          amount: parseFloat(expenseForm.amount),
          date: new Date(expenseForm.date).toISOString(),
        });
        if (res.data.is_duplicate && res.data.matches?.length > 0) {
          setDuplicateWarning({ open: true, matches: res.data.matches, current: {
            description: expenseForm.description, vendor: expenseForm.vendor,
            amount: parseFloat(expenseForm.amount), date: expenseForm.date,
            invoice_number: expenseForm.invoice_number,
          } });
          return;
        }
      } catch (err) { /* ignore check errors, proceed */ }
    }

    // Build submit data, including receipt storage info if available
    const submitData = { ...expenseForm };
    if (submitData._receiptStorage) {
      submitData.receipt_file_key = submitData._receiptStorage.file_key;
      submitData.receipt_filename = submitData._receiptStorage.filename;
      submitData.receipt_content_type = submitData._receiptStorage.content_type;
      submitData.storage_uri = submitData._receiptStorage.storage_uri;
      delete submitData._receiptStorage;
    }
    // Clean internal fields
    Object.keys(submitData).forEach(k => { if (k.startsWith('_')) delete submitData[k]; });

    try {
      if (selectedExpense) {
        await api.put(`/expenses/${selectedExpense.id}`, submitData);
      } else {
        await api.post('/expenses', submitData);
        // Conversion-Funnel (PostHog, August 2026)
        trackEvent('expense_created', { source: 'manual' });
        if (expenses.length === 0) trackEvent('first_expense');
      }
      toast.success(t('common.saved', 'Saved'));
      fetchExpenses();
      setExpenseDialogOpen(false);
      resetExpenseForm();
    } catch (error) {
      console.error("Error saving expense:", error);
      toast.error(t('finances.saveError', 'Error saving'));
    }
  };

  const deleteIncome = async (id) => {
    try {
      await api.delete(`/incomes/${id}`);
      toast.success(t('finances.incomeDeleted', 'Income deleted'));
      fetchIncome();
      setConfirmDialog({ isOpen: false, type: null, itemId: null, title: '', description: '' });
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error(t('common.deleteError', 'Error deleting'));
        fetchIncome();
      } else {
        toast.error(t('common.deleteError', 'Error deleting'));
      }
    }
  };

  const deleteExpense = async (id) => {
    try {
      await api.delete(`/expenses/${id}`);
      toast.success(t('finances.expenseDeleted', 'Expense deleted'));
      fetchExpenses();
      setConfirmDialog({ isOpen: false, type: null, itemId: null, title: '', description: '' });
    } catch (error) {
      toast.error(t('common.deleteError', 'Error deleting'));
    }
  };

  // Juni 2026 — Ausgabe deaktivieren (Soft-Delete statt Löschen, GoBD-Historie bleibt).
  // Pflicht-Grund. Beleg bleibt sichtbar (ausgegraut), zählt aber nicht mehr in UVA/Gewinn/Abschluss/Export.
  const deactivateExpense = async (item) => {
    if (item.treatment === 'capitalized') {
      toast.error('Diese Ausgabe ist als Anlage aktiviert — bitte zuerst die Anlage entfernen.');
      return;
    }
    const reason = window.prompt(
      `Ausgabe „${item.description || item.vendor || 'Ausgabe'}" deaktivieren.\n\nBitte einen Grund angeben (Pflicht). Der Beleg bleibt sichtbar, zählt aber nicht mehr in UVA, Gewinn, Abschluss oder Export.`
    );
    if (reason === null) return;
    if (!reason.trim()) { toast.error('Bitte einen Grund angeben.'); return; }
    try {
      await api.post(`/expenses/${item.id}/deactivate`, { reason: reason.trim() });
      toast.success('Ausgabe deaktiviert');
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Deaktivieren fehlgeschlagen');
    }
  };

  const reactivateExpense = async (item) => {
    try {
      await api.post(`/expenses/${item.id}/reactivate`);
      toast.success('Ausgabe reaktiviert');
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reaktivieren fehlgeschlagen');
    }
  };

  const deactivateIncome = async (item) => {
    const reason = window.prompt(
      `Einnahme „${item.description || 'Einnahme'}" deaktivieren.\n\nBitte einen Grund angeben (Pflicht). Die Einnahme bleibt sichtbar (ausgegraut), zählt aber nicht mehr in UVA, Gewinn, Abschluss oder Export.`
    );
    if (reason === null) return;
    if (!reason.trim()) { toast.error('Bitte einen Grund angeben.'); return; }
    try {
      await api.post(`/incomes/${item.id}/deactivate`, { reason: reason.trim() });
      toast.success('Einnahme deaktiviert');
      fetchIncome();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Deaktivieren fehlgeschlagen');
    }
  };

  const reactivateIncome = async (item) => {
    try {
      await api.post(`/incomes/${item.id}/reactivate`);
      toast.success('Einnahme reaktiviert');
      fetchIncome();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reaktivieren fehlgeschlagen');
    }
  };

  const handleDeleteClick = (id, type) => {
    const isIncome = type === 'income';
    setConfirmDialog({
      isOpen: true,
      type: type,
      itemId: id,
      title: isIncome
        ? t('finances.confirmDeleteIncome', 'Einnahme endgültig löschen?')
        : t('finances.confirmDeleteExpense', 'Ausgabe endgültig löschen?'),
      description: isIncome
        ? 'Diese deaktivierte Einnahme wird unwiderruflich gelöscht. Diese Aktion kann NICHT rückgängig gemacht werden.'
        : 'Diese deaktivierte Ausgabe wird inklusive Beleg unwiderruflich gelöscht. Diese Aktion kann NICHT rückgängig gemacht werden.'
    });
  };

  const handleConfirmAction = () => {
    if (confirmDialog.type === 'income') {
      deleteIncome(confirmDialog.itemId);
    } else if (confirmDialog.type === 'expense') {
      deleteExpense(confirmDialog.itemId);
    }
  };

  // Datum für <input type="date"> normalisieren: ISO-Datetime („2026-01-15T00:00:00+00:00",
  // z.B. aus Migration/Auto-Income) → „2026-01-15", sonst zeigt das Feld LEER an
  // und beim Speichern würde das Datum gelöscht.
  const toDateInput = (d) => (d || "").slice(0, 10);

  const editIncome = (income) => {
    setSelectedIncome(income);
    setIncomeForm({
      description: income.description,
      amount: income.amount,
      date: toDateInput(income.date),
      category: income.category,
      vat_rate: income.vat_rate,
      project_id: income.project_id || ""
    });
    setIncomeDialogOpen(true);
  };

  const editExpense = (expense) => {
    setSelectedExpense(expense);
    setExpenseForm({
      description: expense.description,
      amount: expense.amount,
      date: toDateInput(expense.date),
      category: expense.category,
      vat_rate: expense.vat_rate,
      is_deductible: expense.is_deductible
    });
    setExpenseDialogOpen(true);
  };

  const resetIncomeForm = () => {
    setIncomeForm({
      description: "",
      amount: "",
      date: new Date().toISOString().split('T')[0],
      category: "service",
      vat_rate: 0,
      project_id: ""
    });
    setSelectedIncome(null);
  };

  const resetExpenseForm = () => {
    setExpenseForm({
      description: "",
      amount: "",
      date: new Date().toISOString().split('T')[0],
      category: "office",
      vat_rate: 0,
      is_deductible: true,
      _lowConfidenceAmount: false,
      _lowConfidenceDate: false,
      _lowConfidenceDescription: false
    });
    setSelectedExpense(null);
  };

  const handleMarkNoReceiptNeeded = async (expenseId) => {
    try {
      await api.put(`/expenses/${expenseId}/no-receipt-needed`);
      setExpenses(prev => prev.map(e => e.id === expenseId ? { ...e, no_receipt_needed: true } : e));
      toast.success("Ausgabe als 'Kein Beleg nötig' markiert");
    } catch (e) {
      toast.error("Fehler beim Markieren");
    }
  };

  // Aug 2026 — „Geprüft“-Toggle pro Ausgabe (rein organisatorisch, keine Steuerwirkung)
  const handleToggleReviewed = async (item) => {
    const next = !item.is_reviewed;
    try {
      await api.put(`/expenses/${item.id}`, { is_reviewed: next });
      setExpenses(prev => prev.map(e => e.id === item.id ? { ...e, is_reviewed: next } : e));
      toast.success(next ? "Als geprüft markiert" : "Prüf-Markierung entfernt");
    } catch (e) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleCreateExpenseAndOpenDetail = async () => {
    try {
      const defaultVat = countryInfo?.vat_rates?.[0]?.rate || 19;
      const res = await api.post("/expenses", {
        description: "Neue Ausgabe",
        amount: 0,
        date: new Date().toISOString().split("T")[0],
        category: "other",
        vat_rate: defaultVat,
        is_deductible: true,
      });
      const newExpense = res.data;
      await fetchExpenses();
      setSelectedExpenseDetail(newExpense);
      setExpenseDetailOpen(true);
    } catch (e) {
      toast.error("Fehler beim Erstellen der Ausgabe");
    }
  };



  const handleReceiptUpload = () => {
    // Check if user has Basic or Pro plan
    const planTier = getPlanTier(userPlan);
    if (planTier === 'free') {
      setUpgradePromptOpen(true);
      return;
    }
    
    setReceiptUploadOpen(true);
  };

  const getPlanTier = (plan) => {
    if (plan.includes('basic')) return 'basic';
    if (plan.includes('pro') || plan === 'lifetime') return 'pro';
    return 'free';
  };

  const handleReceiptDataExtracted = async (extractedData) => {
    // Beleg direkt anlegen (inkl. Multi-Steuersatz) und im Detail-Drawer zur Prüfung öffnen.
    // Der Detail-Drawer (ExpenseDetail) unterstützt Multi-Tax voll — daher kein Single-Rate-Verlust.
    try {
      const defaultVat = countryInfo?.vat_rates?.[0]?.rate || 19;
      const payload = {
        description: extractedData.description || 'Gescannter Beleg',
        amount: parseFloat(extractedData.amount) || 0,
        date: extractedData.date ? new Date(extractedData.date).toISOString() : new Date().toISOString(),
        category: extractedData.category_suggestion || extractedData.category || 'other',
        is_deductible: true,
        vendor: extractedData.vendor || null,
        extraction_method: extractedData._extraction_method || 'ai',
        ...buildExpenseTaxFields(extractedData, defaultVat),
      };
      // Bewirtung (§4 EStG): Ort/Restaurant direkt aus der KI-Extraktion übernehmen
      if ((extractedData.category_suggestion || extractedData.category) === 'hospitality') {
        if (extractedData.hospitality_location) payload.hospitality_location = extractedData.hospitality_location;
        if (extractedData.hospitality_occasion) payload.hospitality_occasion = extractedData.hospitality_occasion;
        if (extractedData.hospitality_participants) payload.hospitality_participants = extractedData.hospitality_participants;
      }
      if (extractedData._receiptStorage) {
        payload.receipt_file_key = extractedData._receiptStorage.file_key;
        payload.receipt_filename = extractedData._receiptStorage.filename;
        payload.receipt_content_type = extractedData._receiptStorage.content_type;
        payload.storage_uri = extractedData._receiptStorage.storage_uri;
      }
      const res = await api.post('/expenses', payload);
      await fetchExpenses();
      setReceiptUploadOpen(false);
      setSelectedExpenseDetail(res.data);
      setExpenseDetailOpen(true);

      const rates = (payload.tax_breakdown || []).map((t) => `${t.rate}%`);
      toast.success(rates.length >= 2
        ? `Beleg erfasst · ${rates.length} MwSt-Sätze erkannt (${rates.join(', ')}) – bitte prüfen`
        : 'Beleg erfasst – bitte prüfen');

      if (extractedData.conversion_applied) {
        toast.info(`Fremdwährung konvertiert: ${extractedData.original_amount} ${extractedData.detected_currency} → ${extractedData.amount} ${extractedData.base_currency}`);
      }
    } catch (e) {
      console.error('Error creating expense from receipt:', e);
      toast.error('Fehler beim Erfassen des Belegs');
    }
  };

  // Calculate totals (use year_amount for AfA-distributed expenses, fallback to amount)
  // Brutto-Einnahmen client-side aus sichtbarer Liste (= db.income inkl. manuelle)
  // Juni 2026 — geplante (noch nicht gebuchte) wiederkehrende Zeilen NICHT in Summen/UStVA.
  const bookedExpenses = expenses.filter((item) => !item.is_recurring_pending && !item.deactivated_at);
  // Deaktivierte Einnahmen fließen NICHT in Summen/USt (analog Ausgaben).
  const activeIncome = income.filter((item) => !item.deactivated_at);
  const grossRevenueClient = activeIncome.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
  const grossExpensesClient = bookedExpenses.reduce((sum, item) => sum + parseFloat(item.year_amount ?? item.amount ?? 0), 0);

  // Feb 2026 — UStVA-Konsistenz: Wenn /api/tax/calculate verfügbar, übernimm dessen
  // USt-Werte (= UStVA-Engine, Single Source of Truth). Brutto-Einnahmen bleibt
  // client-side, da die Liste exakt das zeigt, was hier summiert wird.
  const usingUstvaSummary = !!(taxSummary && taxSummary.ustva_engine_used);
  const grossRevenue = usingUstvaSummary && typeof taxSummary.total_income === "number"
    ? taxSummary.total_income
    : grossRevenueClient;
  const grossExpenses = grossExpensesClient;  // Bleibt AfA-spread für Detail-Anzeige

  const vatCollectedClient = activeIncome.reduce((sum, item) => {
    const amount = parseFloat(item.amount || 0);
    const vatRate = parseFloat(item.vat_rate || 0);
    return sum + (amount * vatRate / (100 + vatRate));
  }, 0);
  const vatPaidClient = bookedExpenses.reduce((sum, item) => {
    const amount = parseFloat(item.year_amount ?? item.amount ?? 0);
    const vatRate = parseFloat(item.vat_rate || 0);
    return sum + (amount * vatRate / (100 + vatRate));
  }, 0);

  const vatCollected = usingUstvaSummary ? taxSummary.vat_collected : vatCollectedClient;
  const vatPaid = usingUstvaSummary ? taxSummary.vat_paid : vatPaidClient;

  // Net amounts (without VAT)
  const netRevenue = grossRevenue - vatCollected;
  const netExpenses = grossExpenses - vatPaid;

  // Profits
  const grossProfit = grossRevenue - grossExpenses;
  const netProfit = netRevenue - netExpenses;

  const formatCurrency = (amount) => sharedFormatCurrency(amount, countryInfo?.country || "DE");

  // Juni 2026 — sichtbare Ausgabenliste (gleiche Filter wie unten gerendert) zentral,
  // damit der Detail-Drawer per ↑/↓ durch dieselbe Reihenfolge navigieren kann.
  const visibleExpenses = useMemo(() => {
    const ids = new Set(expenses.map((e) => e.id));
    const base = inboxOnly
      ? [...expenses, ...inboxPending.filter((e) => !ids.has(e.id)).map((e) => ({ ...e, _other_year: true }))]
      : expenses;
    return base
      .filter(item => showLocked || !item.is_locked)
      .filter((item) => {
        const cat = item.deactivated_at
          ? "deactivated"
          : item.is_recurring_pending
            ? "pending"
            : (item._virtual_afa || item.is_afa_portion)
              ? "afa"
              : "booked";
        return expenseFilters.has(cat);
      })
      .filter((item) => !inboxOnly || item.inbox_status === "new")
      .filter((item) => !unreviewedOnly || !item.is_reviewed)
      .filter((item) => expenseMonth === "all" || (item.date || "").substring(5, 7) === expenseMonth)
      .filter((item) => expenseVendor === "all" || (item.vendor || "").trim() === expenseVendor)
      .filter((item) => {
        if (!expenseSearchQuery) return true;
        const q = expenseSearchQuery.toLowerCase();
        return (
          (item.description || "").toLowerCase().includes(q) ||
          (item.vendor || "").toLowerCase().includes(q) ||
          (item.category || "").toLowerCase().includes(q) ||
          String(item.amount).includes(q)
        );
      });
  }, [expenses, inboxPending, showLocked, expenseFilters, expenseSearchQuery, inboxOnly, unreviewedOnly, expenseMonth, expenseVendor]);

  const visibleIncome = useMemo(
    () => income.filter((it) => incomeFilters.has(incomeCat(it))),
    [income, incomeFilters]
  );

  const navIdx = selectedExpenseDetail
    ? visibleExpenses.findIndex(e => e.id === selectedExpenseDetail.id)
    : -1;
  const openExpenseAt = (i) => {
    if (i >= 0 && i < visibleExpenses.length) {
      setSelectedExpenseDetail(visibleExpenses[i]);
      setExpenseDetailOpen(true);
    }
  };

  // Gast-Demo: Finanzen ist read-only. Jeder Klick auf ein Bedien-Element wird
  // zentral abgefangen → kein Drawer/Dialog öffnet, keine Schreib-Aktion läuft;
  // stattdessen der klare "nur im Live-Modus"-Hinweis + "Konto sichern"-Moment.
  const _guestActionSel = 'button, a, [role="button"], [role="switch"], [role="checkbox"], [role="tab"], input, select, textarea';
  const guestFireReadonly = () => {
    try {
      window.dispatchEvent(new CustomEvent('ivoliq:guest-readonly', {
        detail: {
          code: 'guest_readonly',
          message: 'Nur im Live-Modus verfügbar. Sichere dir dein kostenloses Konto, um Finanzen selbst zu erfassen.',
        },
      }));
    } catch { /* ignore */ }
  };
  const guestBlockPointer = (e) => {
    if (!user?.is_guest) return;
    if (!e.target?.closest?.(_guestActionSel)) return;
    // Radix-Trigger (Dialog/Select) öffnen teils schon auf pointerdown → hier stoppen.
    e.preventDefault();
    e.stopPropagation();
  };
  const guestBlockClick = (e) => {
    if (!user?.is_guest) return;
    if (!e.target?.closest?.(_guestActionSel)) return;
    e.preventDefault();
    e.stopPropagation();
    guestFireReadonly();
  };

  return (
    <div className="p-6" onPointerDownCapture={guestBlockPointer} onClickCapture={guestBlockClick}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
            {t('finances.title', 'Finances')} {selectedYear}
          </h1>
          <p className="text-sm text-slate-500">
            {t('finances.subtitle', 'Monitor your revenue, expenses, and profitability')}{selectedYear ? ` für das Jahr ${selectedYear}` : ' (alle Jahre)'}
          </p>
        </div>
        <YearSelector 
          selectedYear={selectedYear}
          onYearChange={setSelectedYear}
          minYear={2020}
          maxYear={2030}
          showAllOption={true}
        />
      </div>

      {/* Mobile-Scan-Promo (Iter 73h: Page-Level, identisch zum Dashboard-Layout) */}
      <MobileScanPromo />

      {/* Feb 2026 — UStVA-Konsistenz-Hinweis + Audit-Breakdown auf EINER Card,
          Hinweis einklappbar (Header klappt Detail auf/zu). Country-aware Paragraphen. */}
      {usingUstvaSummary && taxSummary?.audit_breakdown && (
        <div className="mt-6 mb-4">
          <AuditBreakdownPanel
            breakdown={taxSummary.audit_breakdown}
            currencyFormatter={formatCurrency}
            headerTitle="USt-Werte UStVA-konform"
            headerNote={(
              <>
                Einnahmen-Brutto, USt eingenommen und Vorsteuer stimmen exakt mit deiner Umsatzsteuer-Voranmeldung und dem Steuern-Tab überein.
                Bei aktivierten Anlagen werden Privatanteil und {getLegalRef(countryInfo?.country, "vorsteuer_berichtigung")}-Korrekturen berücksichtigt; Vorsteuer wird gemäß {getLegalRef(countryInfo?.country, "vorsteuer")} voll im Anschaffungsjahr angesetzt.
                {Math.abs(grossRevenue - grossRevenueClient) > 0.5 && (
                  <span className="block mt-1">
                    <strong>Soll-Versteuerung aktiv:</strong> Die Summen-Kachel zeigt alle Rechnungen des Jahres (auch offene). Die Belegliste darunter zeigt nur tatsächliche Zahlungseingänge.
                  </span>
                )}
              </>
            )}
          />
        </div>
      )}

      {/* Financial Overview Cards - Top Row: Revenue & Expenses */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium uppercase tracking-[0.05em] text-slate-500 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-emerald-600" />
              Einnahmen (Gesamt)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Bruttoumsatz (inkl. USt)</span>
                <span className="text-lg font-bold font-mono text-emerald-600">{formatCurrency(grossRevenue)}</span>
              </div>
              <div className="flex justify-between items-center border-t pt-2">
                <span className="text-sm text-gray-600">Nettoumsatz (ohne USt)</span>
                <span className="text-lg font-bold font-mono text-emerald-700">{formatCurrency(netRevenue)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium uppercase tracking-[0.05em] text-slate-500 flex items-center gap-2">
              <TrendingDown className="h-4 w-4 text-rose-600" />
              Ausgaben (Gesamt)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Bruttoausgaben (inkl. USt)</span>
                <span className="text-lg font-bold font-mono text-red-600">{formatCurrency(grossExpenses)}</span>
              </div>
              <div className="flex justify-between items-center border-t pt-2">
                <span className="text-sm text-gray-600">Nettoausgaben (ohne USt)</span>
                <span className="text-lg font-bold font-mono text-red-700">{formatCurrency(netExpenses)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Financial Overview Cards - Bottom Row: Profits */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium uppercase tracking-[0.05em] text-slate-500">Bruttogewinn</CardTitle>
            <p className="text-xs text-gray-500">inkl. USt</p>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold font-mono tracking-tight ${grossProfit >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
              {formatCurrency(grossProfit)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium uppercase tracking-[0.05em] text-slate-500">Nettogewinn</CardTitle>
            <p className="text-xs text-gray-500">ohne USt</p>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold font-mono tracking-tight ${netProfit >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
              {formatCurrency(netProfit)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* USt Overview Card */}
      <Card className="mb-6 border border-gray-200">
        <CardHeader>
          <CardTitle className="text-base font-semibold">Umsatzsteuer-Übersicht</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 border border-gray-200 rounded-xl">
              <div className="text-xs font-medium text-gray-500 mb-1">USt eingenommen</div>
              <div className="text-xl font-bold font-mono text-gray-900">{formatCurrency(vatCollected)}</div>
              <p className="text-xs text-gray-500 mt-2">Aus Brutto-Einnahmen</p>
            </div>
            <div className="p-4 border border-gray-200 rounded-xl">
              <div className="text-xs font-medium text-gray-500 mb-1">Vorsteuer gezahlt</div>
              <div className="text-xl font-bold font-mono text-gray-900">{formatCurrency(vatPaid)}</div>
              <p className="text-xs text-gray-500 mt-2">Aus Brutto-Ausgaben</p>
            </div>
            <div className="p-4 bg-gray-50 border-2 border-gray-900 rounded-xl">
              <div className="text-xs font-semibold text-gray-900 mb-1">USt-Zahllast</div>
              <div className="text-xl font-bold font-mono text-gray-900">{formatCurrency(vatCollected - vatPaid)}</div>
              <p className="text-xs text-gray-600 mt-2">= Eingenommen - Gezahlt</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <IntegrityReviewBanner onChanged={() => { fetchExpenses(); fetchTaxSummary(); }} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
            <CardTitle>{t('finances.income', 'Income')}</CardTitle>
            <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setExternalInvoiceOpen(true)}
              data-testid="new-external-invoice-btn"
            >
              <FileText className="h-4 w-4 mr-2" />
              Externe Rechnung
            </Button>
            <Dialog open={incomeDialogOpen} onOpenChange={(open) => {
              setIncomeDialogOpen(open);
              if (!open) resetIncomeForm();
            }}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  {t('finances.newIncome', 'New Income')}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {selectedIncome ? t('finances.editIncome', 'Edit Income') : t('finances.newIncome', 'New Income')}
                  </DialogTitle>
                </DialogHeader>
                {selectedIncome?.invoice_id && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-xs text-blue-900" data-testid="income-locked-banner">
                    <Lock className="h-3.5 w-3.5 inline mr-1.5" />
                    Diese Einnahme stammt aus einer Rechnung — Beträge, Beschreibung & USt sind dort gepflegt.
                    Hier nur das <strong>Bezahldatum</strong> editierbar.
                    <a
                      href={`/invoices?focus=${selectedIncome.invoice_id}`}
                      className="ml-2 underline hover:text-blue-700"
                      data-testid="income-jump-to-invoice"
                    >
                      → Zur Rechnung springen
                    </a>
                  </div>
                )}
                <form onSubmit={handleIncomeSubmit} className="space-y-4">
                  <div>
                    <Label>{t('finances.description', 'Description')}</Label>
                    <Input
                      value={incomeForm.description}
                      onChange={(e) => setIncomeForm({ ...incomeForm, description: e.target.value })}
                      required
                      readOnly={!!selectedIncome?.invoice_id}
                      className={selectedIncome?.invoice_id ? "bg-slate-50 text-slate-600 cursor-not-allowed" : ""}
                      data-testid="income-description-input"
                    />
                  </div>

                  <div>
                    <Label>{t('finances.amount', 'Amount')}</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={incomeForm.amount}
                      onChange={(e) => setIncomeForm({ ...incomeForm, amount: e.target.value })}
                      required
                      readOnly={!!selectedIncome?.invoice_id}
                      className={selectedIncome?.invoice_id ? "bg-slate-50 text-slate-600 cursor-not-allowed" : ""}
                      data-testid="income-amount-input"
                    />
                  </div>

                  <div>
                    <Label>
                      {selectedIncome?.invoice_id
                        ? "Bezahldatum"
                        : t('finances.date', 'Date')}
                    </Label>
                    <Input
                      type="date"
                      value={incomeForm.date}
                      onChange={(e) => setIncomeForm({ ...incomeForm, date: e.target.value })}
                      required
                      data-testid="income-date-input"
                    />
                  </div>

                  <div>
                    <Label>{t('finances.category', 'Category')}</Label>
                    <Select
                      value={incomeForm.category}
                      onValueChange={(value) => setIncomeForm({ ...incomeForm, category: value })}
                      disabled={!!selectedIncome?.invoice_id}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="service">Service</SelectItem>
                        <SelectItem value="product">Product</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Project ({t('common.optional', 'optional')})</Label>
                    <Select
                      value={incomeForm.project_id || "none"}
                      onValueChange={(value) => setIncomeForm({ ...incomeForm, project_id: value === "none" ? "" : value })}
                      disabled={!!selectedIncome?.invoice_id}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('finances.selectProjectOptional', 'Select project (optional)')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">---</SelectItem>
                        {projects.map((project) => (
                          <SelectItem key={project.id} value={project.id}>
                            {project.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>{t('finances.vatRate', 'VAT Rate')}</Label>
                    <Select
                      value={String(incomeForm.vat_rate)}
                      onValueChange={(value) => setIncomeForm({ ...incomeForm, vat_rate: parseFloat(value) })}
                      disabled={!!selectedIncome?.invoice_id}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">0% ({t('finances.taxFree', 'Tax-free')})</SelectItem>
                        {countryInfo?.vat_rates?.map((rate, index) => (
                          <SelectItem key={index} value={String(rate.rate)}>
                            {rate.rate}% - {rate.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex gap-2 justify-end">
                    <Button type="button" variant="outline" onClick={() => {
                      setIncomeDialogOpen(false);
                      resetIncomeForm();
                    }}>
                      {t('common.cancel', 'Cancel')}
                    </Button>
                    <Button type="submit">
                      {selectedIncome ? t('common.update', 'Update') : t('common.create', 'Create')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {income.length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="income-quickfilters">
                  {[
                    { key: "invoice", label: "Aus Rechnung", count: income.filter(i => incomeCat(i) === "invoice").length },
                    { key: "manual", label: "Manuell", count: income.filter(i => incomeCat(i) === "manual").length },
                    { key: "imported", label: "Importiert", count: income.filter(i => incomeCat(i) === "imported").length },
                    { key: "deactivated", label: "Deaktiviert", count: income.filter(i => incomeCat(i) === "deactivated").length },
                  ].map(({ key, label, count }) => {
                    const active = incomeFilters.has(key);
                    return (
                      <button
                        key={key}
                        onClick={() => toggleIncomeFilter(key)}
                        data-testid={`income-filter-${key}`}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                          active ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"}`}
                      >
                        {active && <Check className="h-3 w-3" />}
                        {label}
                        <span className={`rounded-full px-1.5 ${active ? "bg-emerald-100" : "bg-slate-100"}`}>{count}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              {income.length === 0 ? (
                <p className="text-gray-500 text-center py-4">{t('finances.noIncome', 'Noch keine Einnahmen vorhanden.')}</p>
              ) : visibleIncome.length === 0 ? (
                <p className="text-gray-500 text-center py-4" data-testid="income-empty-filter">Keine Einnahmen für diese Auswahl.</p>
              ) : (
                visibleIncome.map((item) => {
                  const isExternal = item.source === "external_invoice";
                  const openReceipt = async () => {
                    try {
                      const res = await api.get(`/incomes/${item.id}/receipt`, { responseType: "blob" });
                      const url = window.URL.createObjectURL(res.data);
                      window.open(url, "_blank");
                    } catch (err) {
                      toast.error("Beleg konnte nicht geöffnet werden");
                    }
                  };
                  return (
                  <div key={item.id} className={`flex items-center justify-between p-3 bg-gray-50 rounded-xl ${item.deactivated_at ? 'opacity-60' : ''}`} data-testid={`income-row-${item.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <p className="font-medium truncate">{item.description}</p>
                        {item.auto_generated && (
                          <Badge variant="outline" className="text-xs">Aus Rechnung</Badge>
                        )}
                        {isExternal && (
                          <Badge variant="outline" className="text-xs border-blue-300 text-blue-700 bg-blue-50" data-testid={`income-badge-external-${item.id}`}>
                            Extern
                          </Badge>
                        )}
                        {!item.auto_generated && !isExternal && !item.deactivated_at && (
                          <Badge variant="outline" className="text-xs text-slate-500" data-testid={`income-badge-manual-${item.id}`}>
                            Manuell
                          </Badge>
                        )}
                        {item.deactivated_at && (
                          <Badge variant="outline" className="text-xs border-amber-300 text-amber-700 bg-amber-50" data-testid={`income-badge-deactivated-${item.id}`} title={item.deactivated_reason ? `Grund: ${item.deactivated_reason}` : undefined}>
                            Deaktiviert
                          </Badge>
                        )}
                        {!item.date && !item.invoice_date && (
                          <Badge variant="outline" className="text-xs border-red-300 text-red-700 bg-red-50" data-testid={`income-badge-nodate-${item.id}`} title="Ohne Datum wird dieser Eintrag keinem Monat zugeordnet — bitte Datum nachtragen.">
                            Ohne Datum
                          </Badge>
                        )}
                        {item.receipt_file_key && (
                          <button
                            type="button"
                            onClick={openReceipt}
                            title="Beleg öffnen"
                            className="text-slate-400 hover:text-emerald-600"
                            data-testid={`income-receipt-${item.id}`}
                          >
                            <FileText className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">
                        {item.date ? new Date(item.date).toLocaleDateString() : "Datum fehlt"}
                        {isExternal && item.external_invoice_number ? ` · ${item.external_invoice_number}` : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-semibold text-emerald-600">{formatCurrency(item.amount)}</span>
                      {!item.auto_generated && !item.deactivated_at && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => editIncome(item)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => deactivateIncome(item)} title="Einnahme deaktivieren" data-testid={`fl-deactivate-income-${item.id}`}>
                            <Ban className="h-4 w-4 text-amber-600" />
                          </Button>
                        </>
                      )}
                      {!item.auto_generated && item.deactivated_at && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => reactivateIncome(item)} title="Einnahme reaktivieren" data-testid={`fl-reactivate-income-${item.id}`}>
                            <RotateCcw className="h-4 w-4 text-emerald-600" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteClick(item.id, 'income')} title="Deaktivierte Einnahme endgültig löschen" data-testid={`fl-delete-income-${item.id}`}>
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </>
                      )}
                      {item.auto_generated && (
                        <div className="text-xs text-gray-500 px-2">
                          Automatisch erstellt
                        </div>
                      )}
                    </div>
                  </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
            <CardTitle>{t('finances.expenses', 'Expenses')}</CardTitle>
            <div className="flex flex-wrap gap-2">
              {userPlan !== 'free' && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setExportDialogOpen(true)}
                  className="flex items-center gap-2"
                  data-testid="expense-export-btn"
                >
                  <Download className="h-4 w-4" />
                  Export
                </Button>
              )}
              <Button
                size="sm"
                data-testid="new-expense-btn"
                onClick={handleCreateExpenseAndOpenDetail}
              >
                <Plus className="h-4 w-4 mr-2" />
                Ausgabe erfassen
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    data-testid="expense-search"
                    placeholder="Ausgaben durchsuchen..."
                    value={expenseSearchQuery}
                    onChange={(e) => setExpenseSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <button
                  onClick={toggleShowLocked}
                  title={showLocked ? 'Festgeschriebene Perioden ausblenden' : 'Festgeschriebene Perioden anzeigen'}
                  className={`shrink-0 h-10 px-3 rounded-md border text-sm flex items-center gap-1.5 transition-colors ${
                    showLocked ? 'bg-slate-900 text-white border-slate-900 hover:bg-slate-800'
                               : 'bg-white text-slate-600 border-input hover:bg-zinc-50'
                  }`}
                  data-testid="toggle-show-locked-expenses">
                  {showLocked ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                  <span className="hidden sm:inline">Festgeschr. {showLocked ? 'an' : 'aus'}</span>
                </button>
              </div>
              {/* Juni 2026 — Mehrfach-Quickfilter: Gebucht / Ausstehend / AfA (default alle aktiv) */}
              {(() => {
                const counts = {
                  booked: expenses.filter(e => !e.is_recurring_pending && !e._virtual_afa && !e.is_afa_portion && !e.deactivated_at).length,
                  pending: expenses.filter(e => e.is_recurring_pending).length,
                  afa: expenses.filter(e => e._virtual_afa || e.is_afa_portion).length,
                  deactivated: expenses.filter(e => e.deactivated_at).length,
                };
                const chips = [
                  { key: "booked", label: "Gebucht" },
                  { key: "pending", label: "Ausstehend" },
                  { key: "afa", label: "AfA" },
                  { key: "deactivated", label: "Deaktiviert" },
                ];
                return (
                  <div className="flex flex-wrap gap-2" data-testid="expense-quickfilters">
                    {chips.map(({ key, label }) => {
                      const active = expenseFilters.has(key);
                      return (
                        <button
                          key={key}
                          onClick={() => toggleExpenseFilter(key)}
                          aria-pressed={active}
                          data-testid={`expense-filter-${key}`}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                            active ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"}`}
                        >
                          {active && <Check className="h-3 w-3" />}
                          {label}
                          <span className={`rounded-full px-1.5 ${active ? "bg-emerald-100" : "bg-slate-100"}`}>{counts[key]}</span>
                        </button>
                      );
                    })}
                    {/* Juli 2026 — Posteingang-Filter: unbestätigte E-Mail-Belege */}
                    {(() => {
                      const inboxCount = inboxPending.length || expenses.filter(e => e.inbox_status === "new").length;
                      if (inboxCount === 0 && !inboxOnly) return null;
                      return (
                        <button
                          onClick={() => setInboxOnly(v => !v)}
                          aria-pressed={inboxOnly}
                          data-testid="expense-filter-inbox"
                          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                            inboxOnly ? "border-amber-300 bg-amber-50 text-amber-700" : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"}`}
                          title="Nur unbestätigte Belege anzeigen (E-Mail-Eingang, Smartphone-Scan)"
                        >
                          <Mail className="h-3 w-3" />
                          Zu prüfen
                          <span className={`rounded-full px-1.5 ${inboxOnly ? "bg-amber-100" : "bg-slate-100"}`}>{inboxCount}</span>
                        </button>
                      );
                    })()}
                    {/* Aug 2026 — Nur-Ungeprüft-Filter (User-Wunsch) */}
                    {(() => {
                      const unreviewedCount = expenses.filter(e => !e.is_reviewed && !e._virtual_afa && !e.is_recurring_pending && !e.deactivated_at).length;
                      return (
                        <button
                          onClick={() => setUnreviewedOnly(v => !v)}
                          aria-pressed={unreviewedOnly}
                          data-testid="expense-filter-unreviewed"
                          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                            unreviewedOnly ? "border-green-300 bg-green-50 text-green-700" : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"}`}
                          title="Nur Ausgaben anzeigen, die noch nicht als geprüft markiert sind"
                        >
                          <CheckCircle className="h-3 w-3" />
                          Ungeprüft
                          <span className={`rounded-full px-1.5 ${unreviewedOnly ? "bg-green-100" : "bg-slate-100"}`}>{unreviewedCount}</span>
                        </button>
                      );
                    })()}
                    {/* Aug 2026 — Monatsfilter für die Ausgabenliste */}
                    <select
                      value={expenseMonth}
                      onChange={(e) => setExpenseMonth(e.target.value)}
                      data-testid="expense-month-filter"
                      className={`rounded-full border px-2.5 py-1 text-xs font-medium cursor-pointer transition-colors focus:outline-none ${
                        expenseMonth !== "all" ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"}`}
                      title="Ausgaben nach Monat filtern"
                    >
                      <option value="all">Alle Monate</option>
                      {["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"].map((m, i) => (
                        <option key={m} value={String(i + 1).padStart(2, "0")}>{m}</option>
                      ))}
                    </select>
                    {/* Aug 2026 — Lieferanten-Filter (User-Wunsch) */}
                    {(() => {
                      const vendors = [...new Set(expenses.map(e => (e.vendor || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b, "de"));
                      if (vendors.length === 0) return null;
                      return (
                        <select
                          value={expenseVendor}
                          onChange={(e) => setExpenseVendor(e.target.value)}
                          data-testid="expense-vendor-filter"
                          className={`rounded-full border px-2.5 py-1 text-xs font-medium cursor-pointer transition-colors focus:outline-none max-w-[180px] ${
                            expenseVendor !== "all" ? "border-sky-300 bg-sky-50 text-sky-700" : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"}`}
                          title="Ausgaben nach Lieferant filtern"
                        >
                          <option value="all">Alle Lieferanten</option>
                          {vendors.map((v) => (
                            <option key={v} value={v}>{v}</option>
                          ))}
                        </select>
                      );
                    })()}
                  </div>
                );
              })()}
              {/* Batch Receipt Drop Zone */}
              <BatchReceiptDropzone
                compact
                onBatchComplete={() => fetchExpenses()}
              />
              {expenses.length === 0 ? (
                <p className="text-gray-500 text-center py-4">{t('finances.noExpenses', 'Noch keine Ausgaben vorhanden.')}</p>
              ) : (
                (() => {
                  const visible = visibleExpenses;
                  const hiddenLocked = expenses.filter(e => e.is_locked).length - (showLocked ? expenses.filter(e => e.is_locked).length : 0);
                  return (
                    <>
                      {hiddenLocked > 0 && (
                        <div className="text-xs text-slate-500 flex items-center gap-1.5" data-testid="fl-expenses-locked-hint">
                          <Lock className="w-3 h-3" />{hiddenLocked} Ausgabe(n) in festgeschriebenen Perioden ausgeblendet
                        </div>
                      )}
                      {visible.map((item) => {
                    const catInfo = expenseCategories.find((c) => c.key === item.category);
                    const hasComment = !!sbExpenseComments[item.id];
                    return (
                      <div key={item.id} className={`rounded-xl overflow-hidden ${hasComment ? 'border border-blue-200' : ''} ${item.is_locked ? 'opacity-70' : ''}`}>
                      <div
                        data-testid={`expense-row-${item.id}`}
                        className={`flex items-center justify-between p-3 cursor-pointer transition-colors ${hasComment ? 'rounded-none' : 'rounded-xl'} ${
                          item.is_recurring_pending
                            ? 'bg-white hover:bg-violet-50/40 border border-dashed border-violet-200'
                            : item._virtual_afa
                              ? 'bg-emerald-50 hover:bg-emerald-100'
                              : item.is_reviewed && !item.deactivated_at
                                ? 'bg-green-50 hover:bg-green-100 border border-green-200'
                                : 'bg-gray-50 hover:bg-gray-100'
                        }`}
                        onClick={() => {
                          if (item._virtual_afa && item.fixed_asset_id) {
                            navigate(`/anlagen?asset=${item.fixed_asset_id}`);
                            return;
                          }
                          if (item.is_recurring_pending) {
                            // Virtuelle Vorschau-Zeile → Vorlage öffnen
                            const parent = expenses.find((e) => e.id === item.recurrence_parent_id);
                            if (parent) {
                              setSelectedExpenseDetail(parent);
                              setExpenseDetailOpen(true);
                            }
                            return;
                          }
                          setSelectedExpenseDetail(item);
                          setExpenseDetailOpen(true);
                        }}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="flex-shrink-0">
                            {item.is_recurring_pending ? (
                              <div className="w-8 h-8 rounded-full bg-violet-50 border border-dashed border-violet-300 flex items-center justify-center" title="Geplante wiederkehrende Ausgabe – noch nicht gebucht">
                                <Repeat className="h-4 w-4 text-violet-500" />
                              </div>
                            ) : item._virtual_afa ? (
                              <div
                                className="w-8 h-8 rounded bg-emerald-100 flex items-center justify-center"
                                title={`AfA-Buchung aus Anlage · ${item.afa_status === 'posted' ? 'gebucht' : 'geplant'} · Klick öffnet Anlage`}
                                data-testid={`afa-row-icon-${item.fixed_asset_id}`}
                              >
                                <Boxes className="h-4 w-4 text-emerald-700" />
                              </div>
                            ) : item.is_locked ? (
                              <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center" title={`Festgeschrieben: ${item.locked_period_label || ''}`} data-testid={`fl-exp-lock-${item.id}`}>
                                <Lock className="h-4 w-4 text-slate-500" />
                              </div>
                            ) : item.receipt_file_key ? (
                              <div className="w-8 h-8 rounded bg-emerald-100 flex items-center justify-center relative" title={`Beleg vorhanden${item.extraction_method ? ` (${item.extraction_method === 'ai' ? 'KI' : 'OCR'})` : ''}`}>
                                <CheckCircle className="h-4 w-4 text-emerald-600" />
                                {item.extraction_method && (
                                  <span className={`absolute -bottom-1 -right-1 text-[8px] font-bold px-1 rounded ${
                                    item.extraction_method === 'ai' ? 'bg-violet-500 text-white' : 'bg-slate-500 text-white'
                                  }`}>{item.extraction_method === 'ai' ? 'KI' : 'OCR'}</span>
                                )}
                              </div>
                            ) : item.no_receipt_needed ? (
                              <div className="w-8 h-8 rounded bg-slate-50 flex items-center justify-center" title="Kein Beleg nötig">
                                <CheckCircle className="h-4 w-4 text-slate-300" />
                              </div>
                            ) : (
                              <div
                                className="w-8 h-8 rounded bg-amber-50 flex items-center justify-center cursor-pointer hover:bg-amber-100 transition-colors group"
                                title="Kein Beleg nötig markieren"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMarkNoReceiptNeeded(item.id);
                                }}
                              >
                                <XCircle className="h-4 w-4 text-amber-400 group-hover:text-amber-600" />
                              </div>
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium truncate">
                              {item.description}
                              {item.is_afa_portion && (
                                <span className="ml-2 text-xs text-amber-600 font-normal">
                                  (AfA {item.afa_year}/{item.afa_total_years})
                                </span>
                              )}
                              {/* Bewirtungsnachweis §4 Abs. 5 EStG: Warnung bei fehlendem Anlass/Teilnehmer (Ort wird aus Beleg gelesen) */}
                              {item.category === 'hospitality' && (!item.hospitality_occasion || !item.hospitality_participants) && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); setSelectedExpenseDetail(item); setExpenseDetailOpen(true); }}
                                  className="ml-2 inline-flex items-center gap-1 align-middle rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 hover:bg-amber-100 transition-colors"
                                  title="Bewirtungsnachweis unvollständig (§4 Abs. 5 EStG): Anlass & Teilnehmer erforderlich"
                                  data-testid={`expense-hospitality-warning-${item.id}`}
                                >
                                  <AlertTriangle className="h-3 w-3" /> Bewirtungsangaben fehlen
                                </button>
                              )}
                              {/* AfA-Pflicht-Badge für Non-Pro-User (Feb 2026) */}
                              {(() => {
                                const planTier = getPlanTier(userPlan);
                                if (planTier === 'pro') return null;
                                if (item.treatment === 'capitalized' || item.fixed_asset_id) return null;
                                if (item.is_afa_portion) return null;
                                const gross = parseFloat(item.amount || 0);
                                const vat = parseFloat(item.vat_rate || 0);
                                const net = vat > 0 ? gross / (1 + vat / 100) : gross;
                                const country = countryInfo?.country || "DE";
                                const threshold = country === "AT" ? 1000 : country === "CH" ? 999999 : 800;
                                if (net <= threshold) return null;
                                return (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); window.location.href = "/pricing"; }}
                                    className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700 border border-red-300 hover:bg-red-200 transition-colors cursor-pointer"
                                    title={`Netto ${net.toFixed(2)} € > GWG-Grenze (${threshold} €). AfA-pflichtig. Mit Pro: Anlagenspiegel, Buchwert-Tracking, § 15a, Disposal.`}
                                    data-testid="afa-pflicht-badge"
                                  >
                                    ⚠ AfA-pflichtig · Pro
                                  </button>
                                );
                              })()}
                            </p>
                            <div className="flex items-center gap-2 text-sm text-gray-600 flex-wrap">
                              <span>{new Date(item.date).toLocaleDateString()}</span>
                              {catInfo && <Badge variant="outline" className="text-xs py-0">{catInfo.label_de}</Badge>}
                              {item.vendor && <span className="text-slate-400">· {item.vendor}</span>}
                              {/* Juni 2026 — Wiederkehrend-Badge (auffällig, Kreispfeile) + nächste Fälligkeit */}
                              {item.is_recurring && (() => {
                                const next = nextRecurrence(item);
                                return (
                                  <span className="inline-flex items-center gap-1 text-[11px] font-medium text-violet-700 bg-violet-50 border border-violet-200 rounded-full px-2 py-0.5" data-testid="finances-recurring-template" title="Wiederkehrende Ausgabe (Vorlage)">
                                    <Repeat className="h-3 w-3" />
                                    Wiederkehrend
                                    {next ? <span className="text-violet-400">· nächste {next.toLocaleDateString()}</span> : <span className="text-violet-400">· beendet</span>}
                                  </span>
                                );
                              })()}
                              {item.is_recurring_pending && (
                                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-violet-700 bg-violet-50 border border-violet-200 rounded-full px-2 py-0.5" data-testid="finances-recurring-pending" title="Geplant – wird am Fälligkeitstag automatisch gebucht">
                                  <Repeat className="h-3 w-3" /> Geplant
                                </span>
                              )}
                              {item.is_recurring_occurrence && (
                                <span className="inline-flex items-center gap-1 text-[11px] text-slate-400" data-testid="finances-recurring-occurrence" title="Automatisch erzeugte Folgebuchung (kein neuer Beleg)">
                                  <Repeat className="h-3 w-3" /> auto
                                </span>
                              )}
                              {item.deactivated_at && (
                                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid={`finances-deactivated-${item.id}`} title={`Deaktiviert${item.deactivated_by_name ? ` · ${item.deactivated_by_name}` : ''}${item.deactivated_reason ? ` · ${item.deactivated_reason}` : ''} — zählt nicht in UVA/Gewinn/Abschluss/Export`}>
                                  <Ban className="h-3 w-3" /> Deaktiviert{item.deactivated_reason ? `: ${item.deactivated_reason}` : ''}
                                </span>
                              )}
                              {/* Juli 2026 — Beleg-Eingang per E-Mail */}
                              {item.source === 'email_inbox' && (
                                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-2 py-0.5" data-testid={`finances-inbox-badge-${item.id}`} title={`Per E-Mail eingegangen${item.inbound_meta?.from ? ` von ${item.inbound_meta.from}` : ''}`}>
                                  <Mail className="h-3 w-3" /> Per E-Mail
                                </span>
                              )}
                              {/* Juni 2026 — strukturierte E-Rechnung (XRechnung/ZUGFeRD) */}
                              {item.einvoice_format && (
                                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid={`finances-einvoice-badge-${item.id}`} title="Strukturierte E-Rechnung — Beträge und USt exakt aus dem XML übernommen">
                                  <CheckCircle className="h-3 w-3" /> E-Rechnung
                                </span>
                              )}
                              {/* Sep 2026 — Smartphone-Scan: gleicher Prüf-Workflow wie E-Mail-Eingang */}
                              {item.source === 'mobile_scan' && (
                                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-violet-700 bg-violet-50 border border-violet-200 rounded-full px-2 py-0.5" data-testid={`finances-scan-badge-${item.id}`} title="Per Smartphone gescannt (KI-Auslese)">
                                  <Smartphone className="h-3 w-3" /> Smartphone-Scan
                                </span>
                              )}
                              {item.inbox_status === 'new' && (
                                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-300 rounded-full px-2 py-0.5" data-testid={`finances-inbox-review-${item.id}`} title="Automatisch angelegt — bitte prüfen und bestätigen">
                                  <AlertTriangle className="h-3 w-3" /> Prüfen
                                </span>
                              )}
                              {item.inbox_status === 'new' && item.date && !String(item.date).startsWith(String(new Date().getFullYear())) && (
                                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-800 bg-amber-100 border border-amber-300 rounded-full px-2 py-0.5" data-testid={`finances-offyear-badge-${item.id}`} title="Erkanntes Belegdatum liegt nicht im aktuellen Jahr — KI-Lesefehler oder alter Beleg?">
                                  <CalendarClock className="h-3 w-3" /> Datum prüfen
                                </span>
                              )}
                              {item.body_derived && (
                                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-800 bg-amber-100 border border-amber-300 rounded-full px-2 py-0.5" data-testid={`finances-body-derived-${item.id}`} title="Aus dem E-Mail-Text erzeugt — kein Originalbeleg. Bitte prüfen bzw. Original nachreichen.">
                                  Kein Originalbeleg
                                </span>
                              )}
                              {Array.isArray(item.duplicate_of) && item.duplicate_of.length > 0 && !item.duplicate_resolved && !item.deactivated_at && (
                                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-red-700 bg-red-50 border border-red-300 rounded-full px-2 py-0.5" data-testid={`finances-inbox-duplicate-${item.id}`} title="Ein sehr ähnlicher Beleg existiert bereits (Betrag/Datum/Lieferant) — Beleg öffnen, um zu vergleichen und zu entscheiden">
                                  <AlertTriangle className="h-3 w-3" /> Mögliches Duplikat
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <span className={`font-semibold ${item.is_recurring_pending ? 'text-slate-400' : 'text-red-600'}`}>
                            {formatCurrency(item.year_amount ?? item.amount)}</span>
                          {/* Aug 2026 — „Geprüft“-Toggle (User-Wunsch): grüner Haken, Zeile wird grün */}
                          {!item.is_recurring_pending && !item._virtual_afa && !item.deactivated_at && !item.is_locked && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => { e.stopPropagation(); handleToggleReviewed(item); }}
                              title={item.is_reviewed ? "Prüf-Markierung entfernen" : "Als geprüft markieren"}
                              data-testid={`expense-reviewed-toggle-${item.id}`}
                              className={item.is_reviewed ? "text-green-600 hover:text-green-700" : "text-slate-300 hover:text-green-600"}
                            >
                              <CheckCircle className={`h-4 w-4 ${item.is_reviewed ? "fill-green-100" : ""}`} />
                            </Button>
                          )}
                          {!item.is_recurring_pending && (item.modified_by_advisor ? (
                            <TooltipProvider delayDuration={200}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button className="relative p-1 rounded hover:bg-slate-100 transition-colors cursor-help" data-testid="sb-modified-indicator" onClick={(e) => e.stopPropagation()}>
                                    <Edit className="h-4 w-4" />
                                    <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-amber-500 ring-1 ring-white" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="bg-slate-900 text-white text-xs px-2.5 py-1.5 rounded shadow-lg border-0" data-testid="sb-modified-tooltip">
                                  <p>Vom SB geändert{item.modified_by_advisor_name ? ` · ${item.modified_by_advisor_name}` : ''}{item.modified_by_advisor_at ? ` · ${new Date(item.modified_by_advisor_at).toLocaleDateString('de-DE')}` : ''}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); editExpense(item); }}>
                              <Edit className="h-4 w-4" />
                            </Button>
                          ))}
                          {!item.is_recurring_pending && !item.deactivated_at && (
                            <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); deactivateExpense(item); }} title="Ausgabe deaktivieren" data-testid={`fl-deactivate-expense-${item.id}`}>
                              <Ban className="h-4 w-4 text-amber-600" />
                            </Button>
                          )}
                          {item.deactivated_at && !item.is_locked && (
                            <>
                              <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); reactivateExpense(item); }} title="Ausgabe reaktivieren" data-testid={`fl-reactivate-expense-${item.id}`}>
                                <RotateCcw className="h-4 w-4 text-emerald-600" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDeleteClick(item.id, 'expense'); }} title="Deaktivierte Ausgabe endgültig löschen (inkl. Beleg)" data-testid={`fl-delete-expense-${item.id}`}>
                                <Trash2 className="h-4 w-4 text-red-600" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                      {sbExpenseComments[item.id] && (
                        <ExpenseAdvisorComment comment={sbExpenseComments[item.id]} onUpdate={(updated) => {
                          if (updated === null) {
                            setSbExpenseComments(prev => { const n = {...prev}; delete n[item.id]; return n; });
                          } else {
                            setSbExpenseComments(prev => ({...prev, [item.id]: updated}));
                          }
                        }} />
                      )}
                      </div>
                    );
                  })}
                    </>
                  );
                })()
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Receipt Upload Modal */}
      <ReceiptUpload
        isOpen={receiptUploadOpen}
        onClose={() => setReceiptUploadOpen(false)}
        onDataExtracted={handleReceiptDataExtracted}
      />

      {/* Upgrade Prompt Modal */}
      <UpgradePromptModal
        isOpen={upgradePromptOpen}
        onClose={() => setUpgradePromptOpen(false)}
        feature="Beleg-Upload"
      />

      {/* Expense Export Dialog */}
      <ExpenseExportDialog
        isOpen={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
      />

      {/* Expense Detail Sheet */}
      <ExpenseDetail
        expense={selectedExpenseDetail}
        isOpen={expenseDetailOpen}
        onClose={() => {
          setExpenseDetailOpen(false);
          setSelectedExpenseDetail(null);
        }}
        onSaved={() => fetchExpenses()}
        categories={expenseCategories}
        userPlan={userPlan}
        onPrev={() => openExpenseAt(navIdx - 1)}
        onNext={() => openExpenseAt(navIdx + 1)}
        hasPrev={navIdx > 0}
        hasNext={navIdx >= 0 && navIdx < visibleExpenses.length - 1}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, type: null, itemId: null, title: '', description: '' })}
        onConfirm={handleConfirmAction}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmText={t('common.delete', 'Löschen')}
        cancelText={t('common.cancel', 'Abbrechen')}
        variant="destructive"
      />

      {/* Duplicate Warning Dialog */}
      <DuplicateWarningDialog
        isOpen={duplicateWarning.open}
        onClose={() => setDuplicateWarning({ open: false, matches: [] })}
        onConfirm={() => {
          setDuplicateWarning({ open: false, matches: [] });
          handleExpenseSubmit(null, true);
        }}
        matches={duplicateWarning.matches}
        current={duplicateWarning.current}
      />

      <ExternalInvoiceModal
        open={externalInvoiceOpen}
        onOpenChange={setExternalInvoiceOpen}
        countryInfo={countryInfo}
        onSaved={fetchIncome}
      />
    </div>
  );
}


// ─── Advisor Comment per Expense (same pattern as InvoiceAdvisorComments) ───
function ExpenseAdvisorComment({ comment: c, onUpdate }) {
  const [replyingTo, setReplyingTo] = useState(false);
  const [replyText, setReplyText] = useState('');

  const respond = async (action) => {
    try {
      await api.post(`/advisor/comments/${c.id}/reply`, { action });
      onUpdate({ ...c, response_status: action });
    } catch {}
  };

  const sendReply = async () => {
    if (!replyText.trim()) return;
    try {
      await api.post(`/advisor/comments/${c.id}/reply`, { text: replyText.trim() });
      onUpdate({ ...c, reply_text: replyText.trim() });
      setReplyingTo(false);
      setReplyText('');
    } catch {}
  };

  const resolveComment = async () => {
    try {
      await api.post(`/advisor/comments/${c.id}/resolve`);
      onUpdate(null); // Signal removal
    } catch {}
  };

  return (
    <div className="px-3 py-2.5 bg-blue-50/50 border-t border-blue-200" data-testid={`sb-exp-comment-${c.entity_id || c.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <MessageSquare className="w-3 h-3 text-blue-600" />
            <span className="text-[11px] text-slate-500">{c.advisor_name || 'SB'} · {new Date(c.created_at).toLocaleDateString('de-DE')}</span>
          </div>
          <p className="text-sm text-slate-800">{c.text}</p>
          {c.reply_text && (
            <div className="mt-1.5 pl-2.5 border-l-2 border-emerald-300">
              <p className="text-xs text-emerald-700">Ihre Antwort: {c.reply_text}</p>
            </div>
          )}
        </div>
        {c.response_status ? (
          <div className="flex gap-1 shrink-0" onClick={e => e.stopPropagation()}>
            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${c.response_status === 'confirm' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'}`}>
              {c.response_status === 'confirm' ? 'OK' : 'Nein'}
            </span>
            <button onClick={resolveComment} className="text-[10px] px-1.5 py-0.5 rounded border bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200">Erledigt</button>
          </div>
        ) : c.reply_text ? (
          <button onClick={(e) => { e.stopPropagation(); resolveComment(); }} className="text-[10px] px-1.5 py-0.5 rounded border bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200 shrink-0">Erledigt</button>
        ) : (
          <div className="flex gap-1 shrink-0" onClick={e => e.stopPropagation()}>
            <button onClick={() => respond('confirm')} className="text-[10px] px-1.5 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100">OK</button>
            <button onClick={() => respond('reject')} className="text-[10px] px-1.5 py-0.5 rounded border bg-red-50 text-red-600 border-red-200 hover:bg-red-100">Nein</button>
            <button onClick={() => { setReplyingTo(!replyingTo); setReplyText(''); }}
              className={`text-[10px] px-1.5 py-0.5 rounded border ${replyingTo ? 'bg-blue-600 text-white border-blue-600' : 'bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100'}`}>
              Antworten
            </button>
          </div>
        )}
      </div>
      {replyingTo && (
        <div className="mt-2 flex gap-2" onClick={e => e.stopPropagation()}>
          <input type="text" value={replyText} onChange={e => setReplyText(e.target.value)}
            placeholder="Ihre Antwort..." className="flex-1 text-xs border border-slate-300 rounded px-2 py-1.5"
            onKeyDown={e => e.key === 'Enter' && sendReply()} />
          <button onClick={sendReply} disabled={!replyText.trim()}
            className="text-[10px] px-2 py-1.5 rounded bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50">
            Senden
          </button>
        </div>
      )}
    </div>
  );
}
